// Swiss round generation.
//
// Pairings come from bbpPairings, a FIDE-endorsed implementation of the Dutch system compiled
// to WebAssembly (js/services/bbp-wrapper.js). It implements the 2025 Dutch rules.
//
// This replaced JaVaFo 1.4 running on the CheerpJ Java VM. That approach worked, but it had to
// download an entire Java runtime before it could pair a single round: ~6.6s to first pairing
// against ~50ms now, plus a hard dependency on a third-party CDN that every page load blocked
// on — including spectators who never pair anything.
//
// Before the switch both engines were run side by side over real tournaments
// (scripts/replay-tournaments.mjs) and simulated ones (tests/pairing-differential.test.js).
// JaVaFo is still used there as a reference implementation to diff against; it is no longer
// shipped to browsers. Re-run both after any engine update — see vendor/README.md.

import { runDutch, parsePairingList, describeExit, BbpExit, preload as preloadEngine } from './bbp-wrapper.js';

// Every refusal here reaches the arbiter as a dialog, so it is built from a translatable
// template — see TeamEngine.text in js/core/team-engine.js, which also supplies the English
// fallback when the translator is not loaded (node:test).
const say = (key, params) =>
    (typeof window !== 'undefined' && window.TeamEngine)
        ? window.TeamEngine.text(key, params)
        : String(key).replace(/\{(\w+)\}/g, (m, k) => (params && params[k] != null ? params[k] : m));

/**
 * Start fetching the engine ahead of time. Worth calling when a Swiss tournament is opened, so
 * the ~550 KB download happens while the arbiter is still looking at the player list rather
 * than during the moment they are waiting on pairings.
 */
export function preload() {
    return preloadEngine();
}

/**
 * Map engine output (starting numbers) back onto the app's pairing objects.
 *
 * A bye is represented as black 'WO' with result '+/-', matching what the app has always
 * produced, so result entry, standings and TRF export are unaffected.
 */
function toAppPairings(pairs, players) {
    const byNo = new Map(players.map(p => [parseInt(p.startNo, 10), p]));
    const out = [];
    for (const { whiteNo, blackNo } of pairs) {
        const w = byNo.get(whiteNo);
        if (!w) throw new Error(`Engine returned unknown starting number ${whiteNo}`);
        if (blackNo === null) {
            out.push({ white: w.id, black: 'WO', result: '+/-' });
        } else {
            const b = byNo.get(blackNo);
            if (!b) throw new Error(`Engine returned unknown starting number ${blackNo}`);
            out.push({ white: w.id, black: b.id, result: null });
        }
    }
    return out;
}

/**
 * Generate the next round's pairings.
 *
 * A failure is never reported as "zero pairings" — the caller gets a specific reason it can
 * show the arbiter.
 *
 * @param {Object} state
 * @param {Object} [options]  { seedColor } — a 'random' seed must already be resolved
 * @returns {Promise<{ pairings: Array, warnings: string[] }>}
 * @throws {Error} with a human-readable message describing what went wrong
 */
export async function generateNextRound(state, options = {}) {
    // Throws with an actionable message for missing/duplicate starting numbers, an unresolved
    // seed colour, or too few present players.
    const { trf, warnings } = window.PairingTRF.fromState(state, 'bbp', options);

    // Exposed for console inspection when diagnosing a reported pairing.
    window.lastTrfInput = trf;

    const { exitCode, output, stderr } = await runDutch(trf);
    window.lastTrfOutput = output;

    if (exitCode !== BbpExit.OK) {
        throw new Error(describeExit(exitCode, stderr));
    }

    const pairs = parsePairingList(output);
    if (pairs.length === 0) {
        throw new Error(say(
            'The pairing engine returned no pairings. This usually means the tournament data ' +
            'is incomplete — check that every player has a starting number and a result for ' +
            'each played round.'
        ));
    }

    return { pairings: toAppPairings(pairs, state.players), warnings };
}
